import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowRight, Check, Play, X } from 'lucide-react';
import { InspirationGuideVideo } from './inspiration-guide-video';
import { PAGE_GUIDE_MEDIA } from './page-guide-media';
import './page-guide.css';

const INSPIRATION_GUIDE_LESSONS = [
  {
    title: 'Explore Inspiration',
    body: 'Browse Ad templates for creative starting points. Competitors and My Ads hold saved scans, while Find ads has separate search tools.',
    tip: 'This guide is a demonstration. Opening it leaves your current page choices in place.',
    media: 'inspiration-overview',
  },
  {
    title: 'Search for an idea',
    body: 'Use Search inspiration above the filters to narrow the current tab by a keyword, niche, or style.',
    tip: 'If there are no matches, try a shorter term or clear the search.',
    media: 'inspiration-search',
  },
  {
    title: 'Refine your results',
    body: 'Open Add Filter to narrow ads by Niche or Format. The separate Inspiration menu filters creative styles, and the media menu switches between all media, images, and videos.',
    tip: 'Remove a filter chip to loosen the results, or use Reset filters to clear the current search and filters.',
    media: 'inspiration-filters',
  },
  {
    title: 'Inspect an image ad',
    body: 'Choose View on an image ad card to open its details. Review the image, format, and any available niche or style information before using it.',
    tip: 'The actions in the detail view apply to the ad you opened.',
    media: 'inspiration-inspect',
  },
  {
    title: 'Select ads together',
    body: 'Click ad cards to select one or several. The selection bar appears below the library with actions for the chosen ads.',
    tip: 'Select up to ten ads at a time. Choose Clear in the bar to start over.',
    media: 'inspiration-select',
  },
  {
    title: 'Add ads to a canvas',
    body: 'With image ads selected, choose Add to canvas. Pick a task destination to import the images as canvas references without generating new ads.',
    tip: 'The guide only demonstrates the path; it does not add anything to your canvas.',
    media: 'inspiration-canvas',
  },
  {
    title: 'Guide your next ad with a reference',
    body: 'Found a look you like? Open the ad with View, choose Use as reference, and select an existing task. The image will be attached to that task’s brief, ready for your instructions.',
    tip: 'In your brief, describe what you want to borrow from the reference—such as its layout, colors, or composition—and what should be different for your brand.',
    media: 'inspiration-reference',
  },
  {
    title: 'Clone an image ad',
    body: 'Open an image ad with View and choose Clone this ad. You can also clone selected images from the bottom selection bar; two variations are generated per selected image.',
    tip: 'Choose a ready brand task and, if prompted, a product. Selecting a destination starts generation, so this guide stops before that step.',
    media: 'inspiration-clone',
  },
  {
    title: 'Open or download an image',
    body: 'Open an image ad with View, then choose Download to open or download its source image.',
    tip: 'Your browser determines how the source image is saved.',
    media: 'inspiration-download',
  },
  {
    title: 'Organize your inspiration',
    body: 'Use the bookmark action on an ad card to save it to a brand. Open the All Templates menu and choose that brand to browse its collection.',
    tip: 'Brand collections show the templates saved to each brand. They may be empty until you add ads.',
    media: 'inspiration-organize',
  },
];

/** Local, read-only education: never changes library or global onboarding state. */
export function InspirationPageGuide() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const nextRef = useRef<HTMLButtonElement>(null);
  const complete = step === INSPIRATION_GUIDE_LESSONS.length;
  const lesson = INSPIRATION_GUIDE_LESSONS[Math.min(step, INSPIRATION_GUIDE_LESSONS.length - 1)];
  const media = PAGE_GUIDE_MEDIA[lesson.media];
  const changeStep = (next: number) => {
    setStep(Math.max(0, Math.min(INSPIRATION_GUIDE_LESSONS.length, next)));
    nextRef.current?.focus();
  };
  return <Dialog.Root open={open} onOpenChange={value => { setOpen(value); if (value) setStep(0); }}>
    <Dialog.Trigger asChild>
      <button className="page-guide-trigger" type="button" data-testid="inspiration-page-guide"><Play size={14} aria-hidden="true" />Page guide</button>
    </Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="page-guide-overlay" />
      <Dialog.Content className="page-guide-dialog" data-testid="page-guide-dialog" onOpenAutoFocus={event => { event.preventDefault(); nextRef.current?.focus(); }} onKeyDown={event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight' && !complete) { event.preventDefault(); changeStep(step + 1); }
        if (event.key === 'ArrowLeft' && step > 0) { event.preventDefault(); changeStep(step - 1); }
      }}>
        <div className="page-guide-topline">
          <span className="page-guide-number" aria-hidden="true">{complete ? <Check size={16} /> : step + 1}</span>
          <span>Inspiration · Page guide</span>
          <span className="page-guide-count" aria-live="polite" aria-atomic="true">{complete ? 'Complete' : `${step + 1} of ${INSPIRATION_GUIDE_LESSONS.length}`}</span>
          <Dialog.Close asChild><button type="button" className="page-guide-close" aria-label="Close page guide"><X size={18} /></button></Dialog.Close>
        </div>
        <div className="page-guide-body">
          <Dialog.Title className="page-guide-title">{complete ? 'You’re ready to explore.' : lesson.title}</Dialog.Title>
          <Dialog.Description className="page-guide-description">{complete ? 'You can search, inspect, select, and use ads in your next creative task. Your Inspiration page is just as you left it.' : lesson.body}</Dialog.Description>
          {!complete && media && <div className="page-guide-media"><InspirationGuideVideo key={lesson.media} media={media} /></div>}
          {!complete && <p className="page-guide-tip">{lesson.tip}</p>}
          {complete && <button className="page-guide-replay" type="button" onClick={() => changeStep(0)}>Replay this guide</button>}
        </div>
        <div className="page-guide-footer">
          {step > 0 ? <button type="button" className="page-guide-back" onClick={() => changeStep(step - 1)}><ArrowLeft size={14} />Back</button> : <span />}
          <div className="page-guide-footer-actions">
            {!complete && <Dialog.Close asChild><button type="button" className="page-guide-skip">Skip guide</button></Dialog.Close>}
            <button ref={nextRef} type="button" className="page-guide-next" onClick={() => complete ? setOpen(false) : changeStep(step + 1)}>{complete ? 'Explore inspiration' : step === INSPIRATION_GUIDE_LESSONS.length - 1 ? 'Finish' : 'Next'}{!complete && <ArrowRight size={14} />}</button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
